import React from 'react';
import { WifiOff, Maximize, RotateCcw, Download } from 'lucide-react';
import { BuildSettings, WebFeature } from '../../../../core/types/build.types';

interface Props {
  settings: BuildSettings;
  onUpdate: (settings: Partial<BuildSettings>) => void;
}

export const WebSettings: React.FC<Props> = ({ settings, onUpdate }) => {
  const webFeatures: { key: WebFeature; label: string; icon: React.FC<{ size?: number; className?: string }>; description: string }[] = [
    {
      key: 'pwa',
      label: 'Progressive Web App',
      icon: Download,
      description: 'Allow installation as standalone app',
    },
    {
      key: 'offline',
      label: 'Offline Support',
      icon: WifiOff,
      description: 'Cache assets for offline play',
    },
    {
      key: 'fullscreen',
      label: 'Fullscreen Mode',
      icon: Maximize,
      description: 'Enable fullscreen API support',
    },
    {
      key: 'autoOrientation',
      label: 'Auto Orientation',
      icon: RotateCcw,
      description: 'Automatically handle device orientation',
    },
  ];

  const toggleFeature = (feature: WebFeature) => {
    const current = settings.webFeatures || [];
    const updated = current.includes(feature)
      ? current.filter(f => f !== feature)
      : [...current, feature];
    onUpdate({ webFeatures: updated });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-zinc-300">Web Features</h3>

      {/* Feature toggles */}
      <div className="grid grid-cols-2 gap-2">
        {webFeatures.map((feature) => {
          const enabled = settings.webFeatures?.includes(feature.key);
          return (
            <button
              key={feature.key}
              onClick={() => toggleFeature(feature.key)}
              className={`flex items-start gap-2 p-3 rounded-lg text-left transition-colors
                ${enabled
                  ? 'bg-blue-600/10 border border-blue-500/30'
                  : 'bg-zinc-800/50 border border-zinc-700 hover:bg-zinc-800'
                }`}
            >
              <feature.icon
                size={16}
                className={enabled ? 'text-blue-400 mt-0.5' : 'text-zinc-500 mt-0.5'}
              />
              <div>
                <span className={`text-sm font-medium ${enabled ? 'text-blue-300' : 'text-zinc-300'}`}>
                  {feature.label}
                </span>
                <p className="text-xs text-zinc-500 mt-0.5">{feature.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Additional options */}
      <div className="space-y-2 pt-2">
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.serviceWorkerEnabled}
            onChange={(e) => onUpdate({ serviceWorkerEnabled: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600"
          />
          Generate Service Worker
        </label>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.manifestEnabled}
            onChange={(e) => onUpdate({ manifestEnabled: e.target.checked })}
            className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600"
          />
          Generate Web Manifest
        </label>
      </div>
    </div>
  );
};
